import React, { useState } from 'react';
import Marca from '../../components/Marca';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { authService } from '../../services/auth';
import toast from 'react-hot-toast';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Destino que o PrivateRoute guardou antes de mandar para cá.
  //
  // Só aceita caminho interno começando com uma única barra. Um valor como
  // "https://site-falso.com" ou "//site-falso.com" viraria redirecionamento
  // aberto: o usuário faria login no sistema de verdade e seria jogado num
  // clone que pede a senha de novo. O state vem do histórico do navegador e
  // pode ser forjado, então a checagem é obrigatória, não defensiva demais.
  const destinoGuardado = (): string | null => {
    const de = (location.state as { de?: unknown } | null)?.de;

    if (typeof de !== 'string' || !de.startsWith('/') || de.startsWith('//')) {
      return null;
    }
    // Voltar para o próprio login criaria um laço.
    if (de === '/login') {
      return null;
    }
    return de;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authService.login({ email, password });
      if (response.success) {
        const usuario = authService.getUser();
        // Senha provisoria vem antes de qualquer destino: sem a troca, nenhuma
        // outra tela responde.
        if (usuario?.deve_trocar_senha) {
          navigate('/trocar-senha', { replace: true });
        } else {
          toast.success('Login realizado com sucesso!');
          // Motorista vai sempre para o portal dele: o destino guardado é de uma
          // tela de gestão, que o PrivateRoute recusaria em seguida.
          if (usuario?.role === 'motorista') {
            navigate('/motorista');
          } else {
            navigate(destinoGuardado() ?? '/dashboard');
          }
        }
      } else {
        toast.error(response.message || 'Erro ao fazer login');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Credenciais invalidas';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {/* A marca completa ja traz o nome e a assinatura dentro do circulo:
              repetir os dois embaixo escreveria "FROVOX" duas vezes na mesma
              tela. Aqui ela aparece grande, que e onde o texto interno se le. */}
          <Marca versao="completa" tamanho={144} className="mx-auto block w-fit" />
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="seu@email.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Sua senha"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>

          <div className="text-center space-y-2">
            <Link to="/esqueci-senha" className="block text-sm text-blue-600 hover:text-blue-700 font-medium">
              Esqueci minha senha
            </Link>
          </div>

        </form>
      </div>
    </div>
  );
};

export default Login;
